import { TextFieldWithPopover } from '@e2m-customer-portal/ui';
import { Box, Grid, MenuItem, Typography } from '@mui/material';
import { compareAsc, endOfYear, getQuarter, getYear, isWithinInterval, startOfYear, subYears } from 'date-fns';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui-compat';
import { CalendarIcon } from '../../../assets/icons';
import { Maybe } from 'generated/graphql';
import { getAvailableQuarters } from '../utils';

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '400px',
    margin: '0',
    borderRadius: '4px',
    [theme.breakpoints.down('sm')]: {
      width: '240px',
    },
  },
  year: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: theme.spacing(1),
  },
  box: {
    paddingTop: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      borderRight: '0',
    },
  },
  gridItem: {
    margin: '3px auto',
    padding: '0 2px',
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'center',
    borderRadius: '20px',
    padding: '3px 0',
    ...theme.typography.body5,
  },
  selected: {
    background: `${theme.palette.primary.main} !important`,
    color: theme.palette.common.white,
  },
  yearSegment: {
    marginBottom: theme.spacing(2),
    padding: '0 !important',
  },
  input: {
    height: theme.spacing(5),
  },
}));

export interface QuarterSingleSelectorProps {
  marketingStart: Maybe<Date> | undefined;
  marketingEnd: Maybe<Date> | undefined;
  setQuarter: React.Dispatch<React.SetStateAction<Date>>;
}

export function QuarterSingleSelector({
  marketingStart,
  marketingEnd,
  setQuarter,
}: Readonly<QuarterSingleSelectorProps>): ReactElement<QuarterSingleSelectorProps> {
  const { classes } = useStyles();
  const [t] = useTranslation();

  const availableQuarters = getAvailableQuarters(marketingStart, marketingEnd, 'REVENUE_REPORT_TABLE_QUARTER');

  // we return an empty div to avoid an Error if there are no revenue reports from the last two years
  if (!availableQuarters.length) return <></>;

  const lastMarketedQuarter =
    availableQuarters.find(
      (quarter) => getYear(quarter.date) === getYear(new Date()) && getQuarter(quarter.date) === getQuarter(new Date()),
    ) ?? availableQuarters[availableQuarters.length - 1];

  const [selectedQuarter, setSelectedQuarter] = useState(lastMarketedQuarter);

  const lastYear = availableQuarters.filter((quarter) =>
    isWithinInterval(quarter.date, {
      start: startOfYear(subYears(new Date(), 1)),
      end: endOfYear(subYears(new Date(), 1)),
    }),
  );

  const currentYear = availableQuarters.filter((quarter) =>
    isWithinInterval(quarter.date, {
      start: startOfYear(new Date()),
      end: endOfYear(new Date()),
    }),
  );

  const yearSegment = (data: { id: string; date: Date; name: string }[], handleClose: () => void): ReactElement => (
    <>
      {data.map((option) => {
        return (
          <Grid
            key={option.id}
            item
            className={classes.gridItem}
            xs={6}
            onClick={() => {
              if (compareAsc(option.date, new Date()) === 1) {
                return;
              }
              handleClose();
              setSelectedQuarter(option);
              setQuarter(option.date);
            }}
          >
            <MenuItem
              disabled={compareAsc(option.date, new Date()) === 1}
              className={classes.menuItem}
              selected={option.id === selectedQuarter.id}
              classes={{ selected: classes.selected }}
              value={option.name}
            >
              {`${option.name.split(' ')[0]} ${option.name.split(' ')[1]}`}
            </MenuItem>
          </Grid>
        );
      })}
    </>
  );

  return (
    <TextFieldWithPopover
      sx={{ width: '250px', height: '40px' }}
      label={t('QUARTERLY_REVENUE_REPORT_TABLE_DATE_PICKER')}
      value={selectedQuarter.name}
      InputProps={{
        classes: { root: classes.input },
        endAdornment: <CalendarIcon />,
      }}
    >
      {({ handleClose }) => (
        <Grid className={classes.container} container spacing={2}>
          <Grid className={classes.yearSegment} item xs={12} sm={6}>
            <Typography className={classes.year} variant="body4">
              {t('CSV_DOWNLOAD_YEAR', { date: subYears(new Date(), 1) })}
            </Typography>
            <Box
              className={classes.box}
              sx={{
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                padding: (theme) => `${theme.spacing(2)} ${theme.spacing(2)} 0`,
              }}
              display="flex"
              flexDirection="row"
            >
              {yearSegment(lastYear, handleClose)}
            </Box>
          </Grid>
          <Grid className={classes.yearSegment} item xs={12} sm={6}>
            <Typography className={classes.year} variant="body4">
              {t('CSV_DOWNLOAD_YEAR', { date: new Date() })}
            </Typography>
            <Box
              sx={{
                margin: (theme) => `0 ${theme.spacing(2)}`,
              }}
              className={classes.box}
              display="flex"
              flexDirection="row"
            >
              {yearSegment(currentYear, handleClose)}
            </Box>
          </Grid>
        </Grid>
      )}
    </TextFieldWithPopover>
  );
}
